import { Avatar, Box, Button, Stack, styled, Typography } from '@mui/material'
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react'
import LinkedInMessage from './LinkedInMessage'
import { CrossIcon } from '../../assets/inboxHub/InboxHubIcons'
import { InboxHubContext } from '../../pages/InboxHub'
import { useFetchChatMessagesQuery, useSendMessageInChatMutation } from '../../services/unibox-service'
import NoDataAndWaitingWrapper from './NoDataAndWaitingWrapper'
import { chatReducer } from './LinkedInChatBoxHandlers'

function LinkedInChatBox () {
  const [linkedInMessages, dispatch] = useReducer(chatReducer, [])
  const linkedInMessagesLength = useRef()
  const [replyingOfMessageIndex, setReplyingOfMessageIndex] = useState(null)
  const messagesEndRef = useRef()
  const [message, setMessage] = useState({ body: '' })
  const { currentlyOpenedLinkedInChat } = useContext(InboxHubContext)
  const previousChatId = useRef(null)
  const { firstName, lastName, profilePhoto, contactInfo = [] } = currentlyOpenedLinkedInChat.profile

  const {
    data: { items: fetchedMessages = [] } = {},
    isFetching: isFetchingMessages,
    isLoading: isMessagesLoading,
    isError: isMessagesError
  } = useFetchChatMessagesQuery(
    {
      cursor: '',
      limit: '40',
      chatId: currentlyOpenedLinkedInChat.id
    },
    { refetchOnMountOrArgChange: true }
  )

  const [sendMessage] = useSendMessageInChatMutation()

  useEffect(() => {
    if (!fetchedMessages.length) return
    const reversedMessages = fetchedMessages.toReversed()
    const actualMessages = reversedMessages.filter(message => {
      return (message.text?.length && !message.hidden)
    })

    const messagesTransformed = actualMessages.map(
      (message) => {
        const { reply_to: replyTo = {}, text, id, is_sender: isSender, reactions, timestamp } = message
        return (
          {
            id,
            replyOf: replyTo,
            isMe: isSender,
            body: text,
            reaction: reactions,
            photoUrl: isSender ? '' : profilePhoto,
            time: new Date(timestamp)
          }
        )
      }
    )
    dispatch(
      {
        type: 'SetMessages',
        messages: messagesTransformed,
        newChat: previousChatId.current !== currentlyOpenedLinkedInChat.id
      }
    )
    if (previousChatId.current !== currentlyOpenedLinkedInChat.id) {
      previousChatId.current = currentlyOpenedLinkedInChat.id
    }
  }, [fetchedMessages.length])

  function handleTypeMessage (e) {
    setMessage(
      {
        replyOf: null,
        isMe: true,
        body: e.target.value,
        reaction: [],
        photoUrl: ''
      }
    )
  }

  const handleReplyingOfMessage = (messageId) => {
    const idx = linkedInMessages.findIndex(msg => msg.id === messageId)
    setReplyingOfMessageIndex(idx >= 0 ? idx : null)
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (linkedInMessages.length !== linkedInMessagesLength.current) {
      scrollToBottom()
      linkedInMessagesLength.current = linkedInMessages.length
    }
  }, [linkedInMessages.length])

  function handleReactOnMessage (index, reaction) {
    dispatch({
      type: 'Reacted',
      index,
      reaction
    })
  }

  async function handleSendMessage () {
    if (message.body) {
      try {
        const res = await sendMessage(
          {
            chatId: currentlyOpenedLinkedInChat.id,
            text: message.body,
            quoteId: replyingOfMessageIndex >= 0 ? linkedInMessages[replyingOfMessageIndex]?.id : null,
            accountId: 'keGkLW4qRreGsINtJrC7hw'
          }).unwrap()

        dispatch({
          type: 'MessageAdded',
          message: { ...message, replyOf: replyingOfMessageIndex, isMe: true, time: new Date(), id: res.message_id }
        })

        setMessage({ body: '' })
      } catch (error) {
        console.error(error)
      }
    }
    setReplyingOfMessageIndex(null)
  }

  return (
    <Box overflow='hidden' sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', border: '1px solid rgba(228, 228, 229, 1)', borderRadius: 3, background: '#FFFFFF' }}>
      <Stack
        justifyContent='center'
        flex={1}
        sx={
          {
            px: 2,
            backgroundColor: 'rgb(245, 245, 245)',
            width: '100%',
            color: 'rgb(82, 82, 82)',
            borderBottom: '1px solid rgba(228, 228, 229, 1)'
          }
        }
      >
        <Stack direction='row' alignItems='center' gap={1}>
          <Avatar alt='name' src={profilePhoto} />
          <Stack direction='column'>
            <Typography fontSize={16} fontWeight={600}>{`${firstName} ${lastName}`}</Typography>
            <Typography fontSize={10} fontWeight={500}>{contactInfo?.emails?.[0] ?? ''}</Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack flex={14} minHeight={0} p={1} gap={1}>
        <Stack
          flex={14}
          minHeight={0}
          padding={2}
          overflow='auto'
          borderRadius={1}
          gap={2}
          border='1px solid rgb(218, 218, 218)'
          sx={
            {
              backgroundColor: 'rgb(248, 248, 248)',
              '&::-webkit-scrollbar': {
                display: 'none' // Chrome, Safari, Edge
              }
            }
          }
        >
          <NoDataAndWaitingWrapper
            data={fetchedMessages.length}
            isLoading={isMessagesLoading || isFetchingMessages}
            loadingText={`Please Wait getting your chat with ${firstName}`}
            isError={isMessagesError}
            errorText='There some problem getting inboxes, please reload the page'
            noDataText='No Inboxes at the moment'
          >
            {
              [
                linkedInMessages.map((msg, i) => {
                  const showPhoto = linkedInMessages[i - 1]?.isMe !== linkedInMessages[i]?.isMe
                  return (
                    <LinkedInMessage
                      key={i}
                      messageId={msg.id}
                      replyOf={linkedInMessages[msg.replyOf]}
                      time={msg.time}
                      isMe={msg.isMe}
                      body={msg.body}
                      reaction={msg.reaction}
                      showPhoto={showPhoto}
                      photoUrl={msg.photoUrl}
                      handleReplyingOfMessage={handleReplyingOfMessage}
                      handleReactOnMessage={handleReactOnMessage}
                      chatName={`${firstName} ${lastName}`}
                    />
                  )
                }
                ),
                <div key='end-scroll' ref={messagesEndRef} />
              ]
            }
          </NoDataAndWaitingWrapper>
        </Stack>
        {
          replyingOfMessageIndex !== null &&
          (
            <Box
              sx={
                {
                  flex: 1,
                  position: 'relative',
                  backgroundColor: 'rgb(240, 240, 240)',
                  border: '1px solid rgb(231, 231, 231)',
                  py: 1,
                  px: 2,
                  borderRadius: 3,
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    display: 'none' // Chrome, Safari, Edge
                  }
                }
              }
            >
              <Box
                onClick={() => { setReplyingOfMessageIndex(null) }}
                sx={
                  {
                    borderRadius: 10,
                    cursor: 'pointer',
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    translate: '0 -50%'
                  }
                }
              >
                <CrossIcon />
              </Box>
              <Typography fontWeight={600} color='rgb(99, 99, 99)' fontSize='14px'>
                {linkedInMessages[replyingOfMessageIndex]?.isMe ? 'You' : `${firstName} ${lastName}`}
              </Typography>
              <Typography color='rgb(145, 145, 145)' fontSize='12px'>
                {linkedInMessages[replyingOfMessageIndex]?.body}
              </Typography>

            </Box>
          )
        }
        <Stack flex={1} minHeight={0} flexDirection='row' gap={2}>
          <textarea
            value={message.body}
            placeholder='Type your message...'
            onChange={handleTypeMessage}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault() // prevent newline
                handleSendMessage() // call your send function
              }
            }}
            rows={1}
            style={{
              flex: '10',
              height: '100%',
              resize: 'none',
              overflowY: 'auto',
              padding: '10px',
              border: '1px solid rgb(218, 218, 218)',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          />
          <SendButton onClick={() => { handleSendMessage() }}>Send</SendButton>
        </Stack>
      </Stack>

    </Box>

  )
}

export default LinkedInChatBox

export const SendButton = styled(Button)(({ theme, padding }) => ({
  flex: 1,
  color: '#FFF', // Text color
  backgroundColor: '#3F4FF8', // Default background color
  borderRadius: '8px', // Rounded corners
  padding: padding || '8px 16px', // Padding
  transition: 'all 0.3s ease', // Smooth transitions
  fontSize: '16px',
  // Hover styles
  '&:hover': {
    backgroundColor: theme.palette.primary.main, // Background color on hover
    color: 'white', // Text color on hover
    borderColor: theme.palette.primary.dark // Border color on hover
  },

  // Active styles
  '&:active': {
    backgroundColor: theme.palette.primary.dark, // Background color when pressed
    borderColor: theme.palette.primary.dark, // Border color when pressed
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)' // Add shadow on click
  },

  // Disabled styles
  '&.Mui-disabled': {
    color: '#FFF',
    backgroundColor: 'rgb(107, 107, 107)' // Background for disabled state
  }
}))
